import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Collapse,
  IconButton,
  Chip,
  LinearProgress,
  Grid,
  alpha,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Storage as StorageIcon,
  Cloud as CloudIcon,
  SmartToy as AgentIcon,
  Webhook as WebhookIcon,
  Speed as SpeedIcon,
  Dns as DnsIcon,
  Hub as HubIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import ServiceMiniCard from './ServiceMiniCard';

const GROUP_CONFIG = {
  'RabbitMQ Cluster': {
    icon: HubIcon,
    color: '#ff6f00',
    gradient: 'linear-gradient(135deg, #ff6f00 0%, #ff8f00 100%)',
    descKey: 'rabbitmqCluster',
  },
  'Chat Backend': {
    icon: StorageIcon,
    color: '#1976d2',
    gradient: 'linear-gradient(135deg, #1976d2 0%, #42a5f5 100%)',
    descKey: 'chatBackend',
  },
  'RAG Service': {
    icon: CloudIcon,
    color: '#7c4dff',
    gradient: 'linear-gradient(135deg, #7c4dff 0%, #b388ff 100%)',
    descKey: 'ragService',
  },
  'Webhook Service': {
    icon: WebhookIcon,
    color: '#00bcd4',
    gradient: 'linear-gradient(135deg, #00bcd4 0%, #4dd0e1 100%)',
    descKey: 'webhookService',
  },
  'AI Agents': {
    icon: AgentIcon,
    color: '#9c27b0',
    gradient: 'linear-gradient(135deg, #9c27b0 0%, #ce93d8 100%)',
    descKey: 'aiAgents',
  },
  'API Gateway': {
    icon: DnsIcon,
    color: '#3f51b5',
    gradient: 'linear-gradient(135deg, #3f51b5 0%, #7986cb 100%)',
    descKey: 'apiGateway',
  },
  'MCP Server': {
    icon: SpeedIcon,
    color: '#009688',
    gradient: 'linear-gradient(135deg, #009688 0%, #4db6ac 100%)',
    descKey: 'mcpServer',
  },
};

export default function ServiceGroupCard({ groupName, services, defaultExpanded = true }) {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const { t } = useTranslation(['common', 'admin']);
  
  const config = GROUP_CONFIG[groupName] || {
    icon: StorageIcon,
    color: '#757575',
    gradient: 'linear-gradient(135deg, #757575 0%, #9e9e9e 100%)',
    descKey: 'other',
  };
  
  // Translated group name and description
  const translatedGroupName = t(`monitoring.groups.${config.descKey}.name`, { ns: 'admin', defaultValue: groupName });
  const translatedDescription = t(`monitoring.groups.${config.descKey}.desc`, { ns: 'admin', defaultValue: groupName });
  
  const GroupIcon = config.icon;

  // Calculate group health stats
  const totalServices = services.length;
  const healthyServices = services.filter(s => s.status === 'UP').length;
  const degradedServices = services.filter(s => s.status === 'DEGRADED').length;
  const downServices = services.filter(s => s.status === 'DOWN').length;
  const healthPercentage = totalServices > 0 ? (healthyServices / totalServices) * 100 : 0;

  // Get overall group status
  const getGroupStatus = () => {
    if (downServices > 0) return { status: 'DOWN', color: '#ef4444' };
    if (degradedServices > 0) return { status: 'DEGRADED', color: '#f59e0b' };
    if (healthyServices === totalServices) return { status: 'HEALTHY', color: '#10b981' };
    return { status: 'UNKNOWN', color: '#6b7280' };
  };

  const groupStatus = getGroupStatus();

  // Average response time
  const avgResponseTime = services.reduce((acc, s) => {
    return acc + (s.current_response_time || 0);
  }, 0) / services.filter(s => s.current_response_time).length || 0;

  return (
    <Card
      sx={{
        mb: 2,
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
        border: '1px solid',
        borderColor: alpha(config.color, 0.2),
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          background: config.gradient,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: 2,
              bgcolor: 'rgba(255,255,255,0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <GroupIcon sx={{ color: 'white', fontSize: '1.5rem' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ color: 'white', fontWeight: 700, fontSize: '1rem' }}>
              {translatedGroupName}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              {translatedDescription}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Health Summary */}
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Chip
              label={`${healthyServices}/${totalServices}`}
              size="small"
              sx={{
                bgcolor: 'rgba(255,255,255,0.9)',
                color: groupStatus.color,
                fontWeight: 700,
                fontSize: '0.75rem',
              }}
            />
            {avgResponseTime > 0 && (
              <Chip
                label={`${avgResponseTime.toFixed(0)}ms`}
                size="small"
                sx={{
                  bgcolor: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  fontWeight: 600,
                  fontSize: '0.7rem',
                }}
              />
            )}
          </Box>
          
          <IconButton
            size="small"
            sx={{ 
              color: 'white',
              bgcolor: 'rgba(255,255,255,0.1)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' },
            }}
          >
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Progress Bar */}
      <LinearProgress
        variant="determinate"
        value={healthPercentage}
        sx={{
          height: 4,
          bgcolor: alpha(groupStatus.color, 0.2),
          '& .MuiLinearProgress-bar': {
            bgcolor: groupStatus.color,
          },
        }}
      />

      {/* Content */}
      <Collapse in={expanded}>
        <CardContent sx={{ bgcolor: '#fafafa', p: 2 }}>
          <Grid container spacing={2}>
            {services.map((service) => (
              <Grid item xs={12} sm={6} md={4} key={service.name}>
                <ServiceMiniCard service={service} t={t} />
              </Grid>
            ))}
          </Grid>
        </CardContent>
      </Collapse>
    </Card>
  );
}
